import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function Invitasjon() {
  const { token } = useParams();
  const nav = useNavigate();
  const [tilstand, setTilstand] = useState("laster");
  const [bruker, setBruker] = useState(null);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [feil, setFeil] = useState("");
  const [jobber, setJobber] = useState(false);

  useEffect(() => {
    api.get(`/auth/invitasjon/${token}`)
      .then(({ data }) => { setBruker(data); setTilstand("klar"); })
      .catch(() => setTilstand("ugyldig"));
  }, [token]);

  const send = async (e) => {
    e.preventDefault();
    setFeil("");
    if (pw1.length < 8) { setFeil("Passordet må være minst 8 tegn."); return; }
    if (pw1 !== pw2) { setFeil("Passordene er ikke like."); return; }
    setJobber(true);
    try {
      await api.post(`/auth/invitasjon/${token}`, { password: pw1 });
      setTilstand("ferdig");
      setTimeout(() => nav("/admin"), 1200);
    } catch (err) {
      setFeil(formatApiError(err.response?.data?.detail));
      setJobber(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4"
      style={{ background: "#080B0F", fontFamily: "'Instrument Sans', sans-serif" }}
      data-testid="invitasjon-side">
      <div className="w-full max-w-md">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8a8f98] text-center mb-6">
          Norwegian AI Summit — Event OS
        </p>
        <div className="rounded-2xl border border-white/10 bg-[#0e1318] p-8">
          {tilstand === "laster" && <p className="text-sm text-[#8a8f98]" data-testid="invitasjon-laster">Sjekker invitasjonen …</p>}

          {tilstand === "ugyldig" && (
            <div data-testid="invitasjon-ugyldig">
              <h1 className="text-xl font-bold text-[#f5f1e8]" style={{ fontFamily: "Switzer, sans-serif" }}>
                Invitasjonen er ugyldig
              </h1>
              <p className="text-sm text-[#8a8f98] mt-2 leading-relaxed">
                Lenken kan være utløpt eller allerede brukt. Be den som inviterte deg om å sende en ny.
              </p>
            </div>
          )}

          {tilstand === "klar" && bruker && (
            <form onSubmit={send} data-testid="invitasjon-skjema">
              <h1 className="text-2xl font-bold text-[#f5f1e8] tracking-tight" style={{ fontFamily: "Switzer, sans-serif" }}>
                Velkommen, {bruker.name.split(" ")[0]}
              </h1>
              <p className="text-sm text-[#8a8f98] mt-2">
                Velg et passord for <span className="text-[#d7dbe2]">{bruker.email}</span> — så er du inne.
              </p>
              <div className="mt-6 space-y-3">
                <input type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} autoFocus
                  placeholder="Passord (minst 8 tegn)"
                  className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm text-[#f5f1e8] outline-none focus:border-white/30 placeholder:text-[#565c66]"
                  data-testid="invitasjon-passord" />
                <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                  placeholder="Gjenta passord"
                  className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm text-[#f5f1e8] outline-none focus:border-white/30 placeholder:text-[#565c66]"
                  data-testid="invitasjon-passord2" />
              </div>
              {feil && <p className="mt-3 text-sm text-red-400" data-testid="invitasjon-feil">{feil}</p>}
              <button type="submit" disabled={jobber || !pw1 || !pw2}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#f5f1e8] text-[#0c1116] text-sm font-bold px-5 py-3 rounded-xl disabled:opacity-40 transition-opacity"
                data-testid="invitasjon-send">
                <Lock size={14} /> {jobber ? "Lagrer …" : "Velg passord og logg inn"}
              </button>
            </form>
          )}

          {tilstand === "ferdig" && (
            <div data-testid="invitasjon-ferdig">
              <h1 className="text-xl font-bold text-[#f5f1e8]" style={{ fontFamily: "Switzer, sans-serif" }}>
                Du er inne
              </h1>
              <p className="text-sm text-[#8a8f98] mt-2 inline-flex items-center gap-1.5">
                Sender deg til Event OS <ArrowRight size={13} />
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
